import React, { useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { heartbeatSync } from "../../../../State/actions";

// Heartbeat configuration
const HEARTBEAT_CONFIG = {
  INTERVAL: 30000, // 30 seconds
  START_DELAY: 30000, // Wait 30s after init before first heartbeat
  PAUSE_DURING_SPIN: true, // Don't heartbeat during active spins
};

/**
 * HeartbeatManager - Manages periodic balance synchronization
 *
 * Features:
 * - Calls init API every 30 seconds to sync balance
 * - Starts 30 seconds after game initialization
 * - Pauses during active spins to avoid interrupting gameplay
 * - Handles errors gracefully without blocking gameplay
 * - Tracks consecutive failures for error state management
 */
const HeartbeatManager = () => {
  const dispatch = useDispatch();

  // Selectors
  const gameStatus = useSelector((state) => state.game.status);
  const assetsStatus = useSelector((state) => state.assets.status);

  // Refs to persist values across renders
  const intervalRef = useRef(null);
  const startDelayRef = useRef(null);
  const isRunningRef = useRef(false);

  // Check if game is ready to start heartbeat
  const isGameReady =
    assetsStatus === "succeeded" &&
    gameStatus === "succeeded";

  // Check if spin is in progress
  const isSpinning = gameStatus === "spinning";

  /**
   * Execute heartbeat sync
   * Skips if already running or if spin is active
   */
  const executeHeartbeat = useCallback(() => {
    // Skip if previous heartbeat still running
    if (isRunningRef.current) {
      return;
    }

    // Skip if spin is active (pause during gameplay)
    if (HEARTBEAT_CONFIG.PAUSE_DURING_SPIN && isSpinning) {
      return;
    }

    // Execute heartbeat
    isRunningRef.current = true;
    dispatch(heartbeatSync())
      .finally(() => {
        isRunningRef.current = false;
      });
  }, [dispatch, isSpinning]);

  /**
   * Start heartbeat interval
   */
  const startHeartbeat = useCallback(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Start interval
    intervalRef.current = setInterval(() => {
      executeHeartbeat();
    }, HEARTBEAT_CONFIG.INTERVAL);
  }, [executeHeartbeat]);

  /**
   * Stop heartbeat interval
   */
  const stopHeartbeat = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  /**
   * Initialize heartbeat with delay
   */
  useEffect(() => {
    if (!isGameReady) {
      return;
    }

    // Start heartbeat after initial delay
    startDelayRef.current = setTimeout(() => {
      startHeartbeat();
    }, HEARTBEAT_CONFIG.START_DELAY);

    // Cleanup on unmount
    return () => {
      if (startDelayRef.current) {
        clearTimeout(startDelayRef.current);
      }
      stopHeartbeat();
    };
  }, [isGameReady, startHeartbeat, stopHeartbeat]);

  // This component doesn't render anything
  return null;
};

export default React.memo(HeartbeatManager);
